import { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { AlertTriangle, DollarSign, ShoppingCart, Package } from "lucide-react";

import { dashboardService } from "../../services/dashboardService";
import { DashboardSummary } from "../../types/dashboard";
import StatCard from "../../components/dashboard/StatCard";
import OrderStatusChart from "../../components/dashboard/OrderStatusChart";
import RecentOrdersList from "../../components/dashboard/RecentOrdersList";
import SalesChart from "../../components/dashboard/SalesChart";
import TopProductsTable from "../../components/dashboard/TopProductsTable";
import LowStockWidget from "../../components/dashboard/LowStockWidget";
import PageTitle from "../../components/common/PageTitle";

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [timeRange, setTimeRange] = useState<"today" | "week" | "month">(
    "week",
  );

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await dashboardService.getDashboardSummary();

        setSummary(data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount / 100); // Convert cents to dollars
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-t-primary-500 border-r-primary-500/30 border-b-primary-500/30 border-l-primary-500/30 rounded-full animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center max-w-md p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
            Error Loading Dashboard
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <Button color="primary" onPress={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null; // This shouldn't happen if error and loading are handled properly
  }

  // Get the sales data based on the selected time range
  const salesData = {
    today: summary.today_sales,
    week: summary.week_sales,
    month: summary.month_sales,
  }[timeRange];

  return (
    <div className="space-y-6">
      <PageTitle
        subtitle="Overview of your restaurant's performance"
        title="Dashboard"
      />

      {/* Time range selector */}
      <div className="flex space-x-2 mb-6">
        <Button
          color={timeRange === "today" ? "primary" : "default"}
          variant={timeRange === "today" ? "solid" : "light"}
          onPress={() => setTimeRange("today")}
        >
          Today
        </Button>
        <Button
          color={timeRange === "week" ? "primary" : "default"}
          variant={timeRange === "week" ? "solid" : "light"}
          onPress={() => setTimeRange("week")}
        >
          This Week
        </Button>
        <Button
          color={timeRange === "month" ? "primary" : "default"}
          variant={timeRange === "month" ? "solid" : "light"}
          onPress={() => setTimeRange("month")}
        >
          This Month
        </Button>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          change={salesData.compared_to_previous}
          icon={<DollarSign className="h-6 w-6" />}
          title="Total Sales"
          value={formatCurrency(salesData.total_sales)}
        />

        <StatCard
          change={salesData.compared_to_previous}
          icon={<ShoppingCart className="h-6 w-6" />}
          title="Total Orders"
          value={summary.order_status_counts.total.toString()}
        />

        <StatCard
          change={salesData.compared_to_previous}
          icon={<Package className="h-6 w-6" />}
          title="Average Order Value"
          value={formatCurrency(salesData.average_order_value)}
        />
      </div>

      {/* Charts and lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales chart */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Sales Trend</h3>
          <SalesChart data={summary.daily_sales} />
        </div>

        {/* Order status chart */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Order Status</h3>
          <OrderStatusChart data={summary.order_status_counts} />
        </div>
      </div>

      {/* Recent orders and top products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent orders */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Recent Orders</h3>
            <Button as="a" color="primary" href="/orders" variant="light">
              View All
            </Button>
          </div>
          <RecentOrdersList orders={summary.recent_orders} />
        </div>

        {/* Top products */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Top Products</h3>
            <Button as="a" color="primary" href="/reports" variant="light">
              View Report
            </Button>
          </div>
          <TopProductsTable products={summary.top_products.slice(0, 5)} />
        </div>
      </div>

      {/* Low stock alert */}
      {summary.low_stock_items.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Low Stock Items</h3>
            <Button as="a" color="primary" href="/inventory" variant="light">
              View Inventory
            </Button>
          </div>
          <LowStockWidget items={summary.low_stock_items} />
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
